import { RELEASE_CONFIG } from '../config/version';

export interface ReleaseEntry {
  version: string;
  versionFull: string;
  slug: string;
  releaseDate: string;
  title: string;
  summary: string;
  isLatest?: boolean;
  tag: string;
  features: string[];
  improvements: string[];
  bugFixes: string[];
  downloadUrl: string;
  fileSize: string;
  supportedOS: string;
  checksumSha256?: string;
  notes: string;
}

export const RELEASES: ReleaseEntry[] = [
  {
    version: '1.5',
    versionFull: 'v1.5.0',
    slug: 'v1-5',
    releaseDate: 'September 2026',
    title: 'Lashary FB Market Suite v1.5 — Multi-Workspace & AI-Assisted Automation',
    summary: 'Flagship production milestone introducing dedicated vehicle and property workspaces, Facebook number verification automation, AI replies & image generation, Windows SAPI voice notifications, and 2-phase draft safety.',
    isLatest: true,
    tag: 'Current Verified Production Release',
    features: [
      'Specialized Listing Workspaces: Dedicated Vehicle Workspace (Make, Model, Year, Mileage, Transmission, Fuel) and Property Workspace (Bedrooms, Bathrooms, Sq Ft, Property Type) alongside Goods Item listings.',
      'Facebook Number Verification Automation: Automated handling of seller account number confirmation workflows to eliminate operational bottlenecks.',
      'Facebook Account Automation & Creation Workflows: Streamlined account session setup, profile warming pacing, and containerized session storage.',
      'AI-Powered Reply Assistance: Integrated Gemini 2.5 Flash workflow to generate context-aware, professional responses to buyer inquiries.',
      'AI Image Generation & Graphic Optimization: In-suite visual enhancement tools to prepare clean, high-converting product hero imagery.',
      'Windows SAPI Text-to-Speech Voice Alerts: Native speech engine announcing listing milestones, queue progress, and checkpoint status events aloud.',
      'Deterministic Unique-First Image Allocation: Mathematical slot algorithm preventing duplicate photo algorithmic suppression flags.',
      '2-Phase Review-First Draft Safety: Staged privately in isolated draft mode for thorough operator review before public publishing.',
      'Hardware-Backed Credential Vault: Encrypted credential storage bound to your local PC with zero cloud exposure.'
    ],
    improvements: [
      'Local workstation data engine optimized for rapid concurrent draft state reconciliation.',
      'Enhanced humanized jitter timing with randomized delay intervals between listing interactions.',
      'Refined Fluent UI layout with GPU-accelerated transitions and reduced memory overhead.',
      'Expanded browser engine support for latest Chromium and Microsoft Edge stable versions.'
    ],
    bugFixes: [
      'Resolved DOM readiness race condition during rapid Facebook form repaints.',
      'Fixed profile container cleanup on sudden workstation reboot.',
      'Corrected image path parsing for folder paths containing non-ASCII characters.'
    ],
    downloadUrl: RELEASE_CONFIG.downloadUrl,
    fileSize: RELEASE_CONFIG.fileSize,
    supportedOS: RELEASE_CONFIG.supportedOS,
    checksumSha256: RELEASE_CONFIG.checksumSha256,
    notes: 'Recommended update for all active operators. Backwards-compatible with v1.1 local workspace data.'
  },
  {
    version: '1.1',
    versionFull: 'v1.1.0',
    slug: 'v1-1',
    releaseDate: 'September 2026',
    title: 'Lashary FB Market Suite v1.1 — Voice Alerts & Local High-Speed Engine',
    summary: 'Production update introducing Windows SAPI voice alerts, high-speed local data persistence for high-volume catalogs, and Gemini copy synthesis.',
    isLatest: false,
    tag: 'Production Milestone',
    features: [
      'Windows 11 Native Fluent Shell: Fluid desktop interface with GPU acceleration.',
      'High-Performance Storage Engine: Atomic thread-safe persistence for high-volume listing groups.',
      'Gemini AI Copy Formatting: Dynamic bullet point copy synthesis and tag optimization.',
      'Windows SAPI Voice Alerts: Thread-safe native speech synthesizer announcing queue milestones.',
      'Pre-Flight Dry Run Diagnostics: Non-destructive verification of image paths and account login readiness.'
    ],
    improvements: [
      'Reduced memory footprint during large photo batch imports.',
      'Optimized anti-ban delay randomized curves.'
    ],
    bugFixes: [
      'Addressed occasional draft sync timeout on slow network connections.'
    ],
    downloadUrl: RELEASE_CONFIG.downloadUrl,
    fileSize: '81.4 MB',
    supportedOS: 'Windows 10 / 11 (64-Bit)',
    notes: 'Milestone update preceding the v1.5 multi-workspace architecture.'
  },
  {
    version: '1.0',
    versionFull: 'v1.0.0',
    slug: 'v1-0',
    releaseDate: 'June 2026',
    title: 'Lashary FB Market Suite v1.0 — Initial Desktop Release',
    summary: 'The initial validated release of the compiled 64-bit Windows desktop automation suite for Facebook Marketplace.',
    isLatest: false,
    tag: 'Initial Architecture Validation',
    features: [
      'Core Native Browser Automation: Direct Chrome and Edge browser automation without extension hacks.',
      'Review-First 2-Phase Safety: Strict draft-first staging prior to live Marketplace dispatch.',
      'Machine-Bound Credential Security: Hardware-backed session encryption with complete local privacy.',
      'Local Image Allocator: Deterministic unique-first photo assignment to prevent duplicate flags.'
    ],
    improvements: [
      'Initial baseline release.'
    ],
    bugFixes: [
      'Initial baseline release.'
    ],
    downloadUrl: RELEASE_CONFIG.downloadUrl,
    fileSize: '78.2 MB',
    supportedOS: 'Windows 10 / 11 (64-Bit)',
    notes: 'First commercial release of Lashary FB Market Suite.'
  }
];

export const getLatestRelease = (): ReleaseEntry => RELEASES[0];
export const getReleaseBySlug = (slug: string): ReleaseEntry | undefined =>
  RELEASES.find(r => r.slug === slug || r.version === slug);
